import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import backendVerifiedUser from '../../api/backendVerifiedUser';
import { usePermissions } from '../../permissions/PermissionContext';
import ChatBody from './ChatBody';
import useAssistantChat from './useAssistantChat';

export default function AIAssistant() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { canOpen } = usePermissions();
  const [status, setStatus] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [showList, setShowList] = useState(false);

  const loadConversations = useCallback(async () => {
    try {
      const res = await backendVerifiedUser.get('/ai-assistant/conversations');
      setConversations(res.data.success?.data || []);
    } catch {}
  }, []);

  const chat = useAssistantChat({ onRunEnd: loadConversations });
  const { activeId, busy } = chat;

  useEffect(() => {
    backendVerifiedUser
      .get('/ai-assistant/status')
      .then((res) => setStatus(res.data.success))
      .catch(() => setStatus({ is_active: false }));
    loadConversations();
  }, [loadConversations]);

  // /ai-assistant?chat=<id> (from the floating widget's "open full page")
  useEffect(() => {
    const id = searchParams.get('chat');
    if (id) {
      chat.openConversation(id).catch(() => {});
      setSearchParams({}, { replace: true });
    }
  }, []);

  const openConversation = (id) => {
    if (busy) return;
    setShowList(false);
    chat.openConversation(id).catch(() => {});
  };

  const newChat = () => {
    if (busy) return;
    setShowList(false);
    chat.newChat();
  };

  const deleteChat = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this chat?')) return;
    try {
      await backendVerifiedUser.post('/ai-assistant/conversations/delete', { ids: [id] });
      if (id === activeId) newChat();
      loadConversations();
    } catch {}
  };

  const inactive = status && !status.is_active;

  return (
    <div className="mx-3 pb-4">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">AI Assistant</h1>
          <p className="text-sm text-gray-500">
            Ask Claude about orders, stock, sales and coupons{status?.tools?.some((t) => t.write) ? ' — or ask it to make changes' : ''}.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setShowList((v) => !v)} className="md:hidden px-3 py-2 text-sm border border-gray-200 rounded-xl bg-white">
            Chats
          </button>
          <button onClick={newChat} disabled={busy} className="px-3 py-2 text-sm font-medium rounded-xl bg-gray-900 text-white hover:bg-gray-800 disabled:opacity-50">
            + New chat
          </button>
        </div>
      </div>

      {inactive && (
        <div className="mb-4 p-4 rounded-xl bg-amber-50 border border-amber-200 text-sm text-amber-800 flex items-center justify-between gap-3">
          <span>{status.configured ? 'The Claude AI plugin is turned off.' : 'The Claude AI plugin is not set up yet.'}</span>
          {canOpen('/plugins/claude') ? (
            <button onClick={() => navigate('/plugins/claude')} className="shrink-0 px-3 py-1.5 rounded-lg bg-amber-600 text-white text-xs font-medium">
              Open plugin settings
            </button>
          ) : (
            <span className="shrink-0 text-xs">Ask an admin to set it up in Plugins.</span>
          )}
        </div>
      )}

      <div className="flex gap-4 h-[calc(100vh-190px)] min-h-[420px]">
        {/* Conversation list */}
        <aside
          className={`${showList ? 'block' : 'hidden'} md:block w-full md:w-64 shrink-0 bg-white rounded-2xl border border-gray-100 shadow-sm overflow-y-auto`}
        >
          {conversations.length === 0 ? (
            <p className="p-4 text-sm text-gray-400">No chats yet.</p>
          ) : (
            conversations.map((c) => (
              <div
                key={c.id}
                onClick={() => openConversation(c.id)}
                className={`group flex items-center gap-2 px-4 py-3 cursor-pointer border-b border-gray-50 ${c.id === activeId ? 'bg-orange-50' : 'hover:bg-gray-50'}`}
              >
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-gray-800 truncate">{c.title}</p>
                  <p className="text-xs text-gray-400">
                    {new Date(c.updated_at).toLocaleString()}
                    {!!c.has_pending && <span className="ml-1 text-amber-600">• needs approval</span>}
                  </p>
                </div>
                <button
                  onClick={(e) => deleteChat(c.id, e)}
                  className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-rose-600 text-xs"
                  title="Delete chat"
                >
                  ✕
                </button>
              </div>
            ))
          )}
        </aside>

        {/* Chat */}
        <section className={`${showList ? 'hidden' : 'flex'} md:flex flex-1 min-w-0 flex-col bg-white rounded-2xl border border-gray-100 shadow-sm`}>
          <ChatBody chat={chat} inactive={inactive} />
        </section>
      </div>
    </div>
  );
}
